import _ from 'underscore';
import { DeterministicView } from '@bingads-webui/deterministic-view';
import {
  entityHash,
  navigateToEntity,
  getEntityIds,
  coerceStatus,
  topChangesGridTemplate,
  getInsightsMetric,
} from '@bingads-webui-campaign/overview-cards-common';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { createElementBasedFluctuationPopupFactory } from '@bingads-webui-campaign-react/fluctuation-popup';

/* @this TopChangeGrid */
function processGridData({
  metric,
  data,
  entitySet,
}) {
  const {
    pageContext,
    legacyRouter,
    metricFormatter,
    i18n,
    instrumentation,
    constants,
  } = this.deps;


  if (data === undefined || data === null) {
    return undefined;
  } else if (_.size(data) === 0) {
    return [];
  }

  _.each(data, (entity) => {
    if (!entity.PerformanceMetricsChange) {
      instrumentation.logMessage([constants.LOGCATEGORY.ERROR], constants.LOGLEVEL.INFO, 'top-changes-card', JSON.stringify(entity));
    }
  });

  const maxChange = Math.abs(_.max(data, ({
    PerformanceMetricsChange: { [metric]: value },
  }) => Math.abs(value)).PerformanceMetricsChange[metric]);

  return _.map(data, (entity) => {
    const formattedPeriodMetrics = metricFormatter.formatMetric(
      metric,
      entity.PeriodPerformanceMetrics[metric],
      { currency: entity.Currency }
    );
    const formattedMetrics = metricFormatter.formatMetric(
      metric,
      entity.PerformanceMetrics[metric],
      { currency: entity.Currency }
    );
    const value = entity.PerformanceMetricsChange[metric];
    let declineBar = 0;
    let declineSpace = 100;
    let increaseBar = 0;
    let increaseSpace = 100;

    if (maxChange !== 0) {
      if (value < 0) {
        declineBar = Math.ceil(-(value * 100) / maxChange);
        declineSpace = 100 - declineBar;
      } else {
        increaseBar = Math.ceil((value * 100) / maxChange);
        increaseSpace = 100 - increaseBar;
      }
    }

    const typeName = entitySet.type.elementType.shortName;
    const { campaignId, adgroupId, accountId } = getEntityIds({
      typeName,
      id: entity.Id,
      entitySet,
    });

    let isExperiment;

    if (typeName === 'Campaign') {
      isExperiment = !!entity.ExperimentId;
    } else if (typeName === 'AdGroup') {
      isExperiment = !!entity.Campaign.ExperimentId;
    }

    const fluctuationTypeIds = getInsightsMetric(entity.AvailableInsightTypeIds, metric);

    return {
      name: entity.Name,
      status: coerceStatus(entity.Status, entity.AccountInactiveReasons),
      value,
      formattedValue: metricFormatter.formatMetricWithSign(
        metric,
        entity.PerformanceMetricsChange[metric],
        { currency: entity.Currency }
      ),
      formattedPercentageValue: metricFormatter
        .formatChangePercentageWithSign(entity.PerformanceMetricsChangePercentage[metric]),
      fromTo: i18n.getString('Dashboard_Cards_FromTo', { fromValue: formattedPeriodMetrics, toValue: formattedMetrics }),
      hash: entityHash({
        typeName,
        id: entity.Id,
        isSmartCampaign: entity.CampaignType && entity.CampaignType === 'Smart',
        entitySet,
        pageContext,
        legacyRouter,
        status: entity.Status,
      }),
      campaignType: entity.CampaignType,
      campaignId,
      adgroupId,
      accountId,
      typeName,
      formattedMetrics,
      formattedPeriodMetrics,
      declineBar,
      declineSpace,
      increaseBar,
      increaseSpace,
      isExperiment,
      rawStatus: entity.Status,
      fluctuationTypeIds,
    };
  });
}

export default class TopChangeGrid extends DeterministicView {
  constructor(options) {
    super(_.extend({}, options, { renderInSync: true }));
  }

  initialize({
    deps,
    perfMarker,
  }) {
    this.deps = deps;
    this.perfMarker = perfMarker;
  }

  get className() {
    return 'top-change-grid-container';
  }

  get events() {
    const { pageContext, legacyRouter, createEditableFilterStorage } = this.deps;

    return {
      'click .top-change-entity-name a': event => navigateToEntity({
        event,
        pageContext,
        legacyRouter,
        createEditableFilterStorage,
        cardType: 'top-changes-card',
        instrumentation: this.deps.instrumentation,
        constants: this.deps.constants,
      }),
      'click .mcc-perf-fluctuation-bulb': event => this.showFluctuationPopup(event),
      'keypress .mcc-perf-fluctuation-bulb': event => this.showFluctuationPopup(event),
    };
  }

  showFluctuationPopup(e) {
    const triggerButton = e.currentTarget;
    const placeholder = e.currentTarget.nextSibling;
    const dataSet = e.currentTarget.dataset;
    const {
      fluxtypeids = [], metricname, metrictext, cmid, aid,
    } = dataSet || {};
    const { dateRange } = this.getState();
    const selectedAccount = aid ? { CurrentAccount: { Id: Number(aid) || 0 } } : {};
    const selectedCampaign = cmid ? { CurrentCampaign: { Id: Number(cmid) || 0 } } : {};
    const pageContextWithMetricRowInfo = {
      ...this.deps.pageContext,
      ...selectedAccount,
      ...selectedCampaign,
      ...this.deps.context,
    };

    if (placeholder.className === 'mcc-perf-fluctuation-placeholder') {
      createElementBasedFluctuationPopupFactory({
        triggerButton,
        deps: {
          ...this.deps,
          // campaignui/feature/instrumentation/index
          instrumentation: this.deps.localInstrumentation,
          // TODO: update imagePath with newest resource URL
          imagePath: 'https://RESOURCES.UI.BINGADS.MICROSOFT.COM/AdvertiserUI/V2018.1030.2248.41/Application/Images/fluctuation-bulb.png',
          token: this.deps.pageContext.Token,
          context: pageContextWithMetricRowInfo,
          appConfig: this.deps.config,
        },
        metrics: [{
          name: metricname,
          value: metrictext,
        }],
        canViewAllInsights: true,
        asyncLoadFluctuationConfig: {
          dateRange: {
            startDate: dateRange.start,
            endDate: dateRange.end,
          },
          fluctuationTypeIds: JSON.parse(fluxtypeids) || [],
        },
        fromScenario: cmid ? 'CampaignsOverview' : 'AccountsOverview',
        onFlucDismiss: ({ isLastFluc }) => {
          if (isLastFluc) {
            DeterministicView.getParent(this).setState({ dismissLastFluctuation: true });
          }
        },
        onDismiss: () => {
          if (triggerButton) {
            triggerButton.focus();
          }
        },
        perfMarker: this.perfMarker,
      }, placeholder);
    }
  }

  template() {
    const {
      metric,
      data,
      entitySet,
      currentDateRangeText,
      comparisonDateRangeText,
      hasError,
      loaded,
    } = this.getState();

    let gridData;

    if (!hasError) {
      gridData = processGridData.call(this, {
        metric,
        data: _.compact(data),
        entitySet,
      });
    }

    return () => topChangesGridTemplate({
      hasError,
      loaded,
      gridData,
      i18n: this.deps.i18n,
      metricName: metric ? this.deps.s$i18n.property('PerformanceMetrics', metric) : '',
      currentDateRangeText,
      comparisonDateRangeText,
      AccountActivityStatus: Constants.AccountActivityStatus,
    });
  }

  renderTrunk() {
    const { loaded } = this.getState();
    const parent = DeterministicView.getParent(this);

    if (loaded) {
      parent.willRenderWithData();
    }
    super.renderTrunk();
    if (loaded) {
      parent.renderedWithData();
    }
  }
}
